import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { useChangePassword, useUpdateMe } from "@/api/queries";
import { useAuth, useMe } from "@/app/auth";
import { Field, PasswordInput } from "@/components/common/Field";
import { TimezoneSelect } from "@/components/common/TimezoneSelect";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input, NativeSelect } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { languages, type Language } from "@/i18n";
import { errorText, fieldError } from "@/lib/errors";

import { Section } from "./SettingsLayout";

export function ProfilePage() {
  return (
    <Card className="px-6 py-6">
      <ProfileForm />
      <NotificationsForm />
      <PasswordForm />
    </Card>
  );
}

function ProfileForm() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const auth = useAuth();
  const update = useUpdateMe();
  const [fullName, setFullName] = useState(me.user.full_name);
  const [timezone, setTimezone] = useState(me.user.timezone);
  const [locale, setLocale] = useState<Language>(me.user.locale);
  const [saved, setSaved] = useState(false);
  const dirty =
    fullName.trim() !== me.user.full_name || timezone !== me.user.timezone || locale !== me.user.locale;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    try {
      const next = await update.mutateAsync({ full_name: fullName.trim(), timezone, locale });
      auth.setMe(next);
      if (i18n.resolvedLanguage !== next.user.locale) await i18n.changeLanguage(next.user.locale);
      setSaved(true);
    } catch {
      // Shown below.
    }
  };

  return (
    <Section title={t("settings.profile.title")} description={t("settings.profile.description")}>
      <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)}>
        <Field label={t("settings.profile.fullName")} error={fieldError(t, update.error, "full_name")}>
          <Input
            required
            maxLength={120}
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("settings.profile.email")} hint={t("settings.profile.emailHint")}>
            <Input value={me.user.email} readOnly disabled />
          </Field>
          <Field label={t("settings.profile.role")}>
            <Input value={t(`roles.${me.user.role}`)} readOnly disabled />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <Field label={t("settings.profile.timezone")}>
            <TimezoneSelect value={timezone} onChange={(e) => setTimezone(e.target.value)} />
          </Field>
          <Field label={t("settings.profile.language")}>
            <NativeSelect
              className="h-10"
              value={locale}
              onChange={(e) => setLocale(e.target.value as Language)}
            >
              {languages.map((l) => (
                <option key={l} value={l}>
                  {t(`languages.${l}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </div>
        {update.error && !fieldError(t, update.error, "full_name") && (
          <Alert variant="danger">{errorText(t, update.error)}</Alert>
        )}
        {saved && !dirty && <Alert variant="success">{t("settings.profile.saved")}</Alert>}
        <div>
          <Button type="submit" disabled={update.isPending || !dirty || !fullName.trim()}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Section>
  );
}

function NotificationsForm() {
  const { t } = useTranslation();
  const me = useMe();
  const auth = useAuth();
  const update = useUpdateMe();
  const id = "email-notifications";
  return (
    <Section title={t("settings.notifications.title")} description={t("settings.notifications.description")}>
      <div className="flex items-start gap-3">
        <Switch
          id={id}
          checked={me.email_notifications}
          disabled={update.isPending}
          onCheckedChange={(checked) =>
            update.mutate({ email_notifications: checked }, { onSuccess: (next) => auth.setMe(next) })
          }
        />
        <label htmlFor={id} className="flex flex-col">
          <span className="font-medium">{t("settings.notifications.email")}</span>
          <span className="text-xs text-muted">{t("settings.notifications.emailHint")}</span>
        </label>
      </div>
      <div className="mt-4 flex items-start gap-3">
        <Switch
          id={`${id}-digest`}
          checked={me.weekly_digest}
          disabled={update.isPending}
          onCheckedChange={(checked) =>
            update.mutate({ weekly_digest: checked }, { onSuccess: (next) => auth.setMe(next) })
          }
        />
        <label htmlFor={`${id}-digest`} className="flex flex-col">
          <span className="font-medium">{t("settings.notifications.digest")}</span>
          <span className="text-xs text-muted">{t("settings.notifications.digestHint")}</span>
        </label>
      </div>
      {update.error && (
        <Alert variant="danger" className="mt-3">
          {errorText(t, update.error)}
        </Alert>
      )}
    </Section>
  );
}

function PasswordForm() {
  const { t } = useTranslation();
  const me = useMe();
  const change = useChangePassword();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const mismatch = confirm !== "" && confirm !== next;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setDone(false);
    try {
      await change.mutateAsync({ current_password: current, new_password: next });
      setCurrent("");
      setNext("");
      setConfirm("");
      setDone(true);
    } catch {
      // Shown below.
    }
  };

  const err = change.error;
  return (
    <Section title={t("settings.password.title")} description={t("settings.password.description")}>
      <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)}>
        {/* Helps password managers pick the right account. */}
        <input type="email" value={me.user.email} autoComplete="username" readOnly hidden />
        <Field label={t("settings.password.current")} error={fieldError(t, err, "current_password")}>
          <PasswordInput
            required
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("settings.password.new")}
            hint={t("password.hint")}
            error={fieldError(t, err, "new_password")}
          >
            <PasswordInput
              required
              minLength={10}
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </Field>
          <Field label={t("password.confirm")} error={mismatch ? t("password.mismatch") : undefined}>
            <PasswordInput
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>
        </div>
        {err && !fieldError(t, err, "current_password") && !fieldError(t, err, "new_password") && (
          <Alert variant="danger">{errorText(t, err)}</Alert>
        )}
        {done && <Alert variant="success">{t("settings.password.changed")}</Alert>}
        <div>
          <Button
            type="submit"
            disabled={change.isPending || !current || next.length < 10 || next !== confirm}
          >
            {t("settings.password.submit")}
          </Button>
        </div>
      </form>
    </Section>
  );
}
